import { mercuryApi } from '$api'
import { Country, PayrollSystem } from '$constants'
import {
  StaffMember,
  StaffMemberDetail,
  StaffMemberRemunerationSnapshot,
  transformRemunerationSnapshotOnGet,
  transformRemunerationSnapshotOnSave,
  transformStaffMemberOnGet,
  transformStaffMemberOnSave,
} from '$models'
import { createQueryString, getActiveCompanySettings } from '$utils'
import { Dayjs } from 'dayjs'

/**
 * Get all Staff Members
 * @param includeDeleted - include the ones with datestampDeleted = true
 * @param time - include all the staff member until a specific time
 * @returns Promise<StaffMemberDetail[]>
 */
export const getAll = (
  time?: Dayjs,
  includeDeleted: boolean = true
): Promise<StaffMemberDetail[]> => {
  const queryString = createQueryString({
    time: time?.utc().format(),
    includeDeleted: includeDeleted,
  })

  return mercuryApi.get<StaffMemberDetail[]>(`/staff-members?${queryString}`)
}

/**
 * Get Staff Member by id
 * @param staffMemberId - staff member id to get
 * @returns Promise<StaffMember>
 */
export const getById = async (staffMemberId: string): Promise<StaffMember> => {
  const { payrollSystemPreference, countryPreference } =
    getActiveCompanySettings()

  const result = await mercuryApi.get<StaffMember>(
    `/staff-members/${staffMemberId}`
  )
  const updatedStaffMember = transformStaffMemberOnGet(
    result,
    payrollSystemPreference,
    countryPreference
  )

  return updatedStaffMember
}

/**
 * Get Staff Member by id
 * @param staffMemberId - staff member id to get
 * @param country - company country received through query string params
 * @param payrollSystem - company payroll system received through query string params
 * @returns Promise<T>
 */
export const getPersonalDetailsById = async (
  staffMemberId: string,
  country?: Country,
  payrollSystem?: PayrollSystem
): Promise<StaffMember> => {
  const result = await mercuryApi.get<StaffMember>(
    `/staff-members/personal-details/${staffMemberId}`
  )

  const updatedStaffMember = transformStaffMemberOnGet(
    result,
    payrollSystem,
    country
  )
  return updatedStaffMember
}

/**
 * Update staff member
 * @param staffMember - staff member to update
 * @returns Promise<T>
 */
export const update = async (
  staffMember: StaffMember
): Promise<StaffMember> => {
  const { payrollSystemPreference, countryPreference } =
    getActiveCompanySettings()

  const transformedStaffMember: object = transformStaffMemberOnSave(
    payrollSystemPreference,
    staffMember,
    countryPreference
  )
  const updatedStaffMember = await mercuryApi.update(
    `/staff-members/${staffMember.id}`,
    transformedStaffMember
  )

  const transformedStaffMemberWhenGet = transformStaffMemberOnGet(
    updatedStaffMember,
    payrollSystemPreference,
    countryPreference
  )

  return transformedStaffMemberWhenGet
}

/**
 * Update staff member
 * @param staffMember - staff member to update
 * @param country - company country received through query string params
 * @param payrollSystem - company payroll system received through query string params
 * @returns Promise<T>
 */
export const updatePersonalDetails = async (
  staffMember: StaffMember,
  country: Country,
  payrollSystem: PayrollSystem
): Promise<StaffMember> => {
  const transformedStaffMember: object = transformStaffMemberOnSave(
    payrollSystem,
    staffMember,
    country
  )
  const updatedStaffMember = await mercuryApi.update(
    `/staff-members/personal-details/${staffMember.id}`,
    transformedStaffMember
  )

  const transformedStaffMemberWhenGet = transformStaffMemberOnGet(
    updatedStaffMember,
    payrollSystem,
    country
  )

  return transformedStaffMemberWhenGet
}

/**
 * Add staff member
 * @param staffMember - staff member to add
 * @returns Promise<T>
 */
export const add = async (staffMember: StaffMember): Promise<StaffMember> => {
  const { payrollSystemPreference, countryPreference } =
    getActiveCompanySettings()

  const transformedStaffMember: object = transformStaffMemberOnSave(
    payrollSystemPreference,
    staffMember,
    countryPreference
  )

  const addedStaffMember = await mercuryApi.add(
    `/staff-members`,
    transformedStaffMember
  )

  const transformedStaffMemberWhenGet = transformStaffMemberOnGet(
    addedStaffMember,
    payrollSystemPreference,
    countryPreference
  )

  return transformedStaffMemberWhenGet
}

/**
 * Add remuneration snapshot to a staff member
 * @param remunerationSnapshot - remuneration snapshot to add
 * @returns Promise<StaffMemberRemunerationSnapshot>
 */
export const addRemunerationSnapshot = async (
  remunerationSnapshot: StaffMemberRemunerationSnapshot
): Promise<StaffMemberRemunerationSnapshot> => {
  const transformedRemunerationSnapshot =
    transformRemunerationSnapshotOnSave(remunerationSnapshot)

  const newRemunerationSnapshot = await mercuryApi.add(
    '/staff-members/remuneration',
    transformedRemunerationSnapshot
  )

  return transformRemunerationSnapshotOnGet(newRemunerationSnapshot)
}

/**
 * Update staff members emails
 * @param updatedEmails - list of tuples of [staffMemberId, email]
 * @returns Promise<boolean>
 */
export const updateStaffMemberEmails = async (
  staffMemberEmails: [string, string][]
): Promise<boolean> => {
  const convertedStaffMemberEmails = staffMemberEmails.map(([id, email]) => ({
    id: id,
    email: email,
  }))

  return await mercuryApi.update('/staff-members/update-emails', {
    updatedEmails: convertedStaffMemberEmails,
  })
}

/**
 * Resend a staff member onboarding request
 * @param staffMemberId - staff member id
 * @returns Promise<boolean>
 */
export const resendStaffMemberOnboaringRequest = async (
  staffMemberId: string
): Promise<boolean> => {
  return await mercuryApi.add(
    `/staff-members/${staffMemberId}/onboarding-request`,
    {}
  )
}
